import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import PropTypes from 'prop-types';
import { useMutation } from '@apollo/client';
import { useHistory } from 'react-router';

import Modal from 'components/ui/Modal';
import { ConfirmButton } from 'components/ui/Button';
import { UPDATE_PRR_ATTEMPTED_COMPENSATION_RECORDS } from 'store/mutations';

import { ButtonWrapper } from './PrrThroughFareConfirmation.styles';

const PrrAttemptedConfirmation = ({ compensationRecords, onClose, onContinue }) => {
  const history = useHistory();
  const [failedRecords, setFailedRecords] = useState([]);

  const [updatePrrAttemptedCompensationRecords, { loading }] = useMutation(UPDATE_PRR_ATTEMPTED_COMPENSATION_RECORDS, {
    onCompleted: (data) => {
      const { updatePrrAttemptedCompensationRecords = [] } = data ?? {};

      onClose();

      const failedTickets = [];
      const updatedTickets = [];

      compensationRecords.forEach(({ compensationRecordId, ticketNumber }) => {
        if (updatePrrAttemptedCompensationRecords.includes(compensationRecordId)) {
          updatedTickets.push(ticketNumber);
        } else {
          failedTickets.push(ticketNumber);
        }
      });

      if (failedTickets.length > 0) {
        setFailedRecords([failedTickets, updatedTickets]);

        return;
      }

      history.go(0);
    },
    onError: () => {
      onClose();
      setFailedRecords([compensationRecords.map(({ ticketNumber }) => ticketNumber)]);
    }
  });

  const handleUpdateRecords = () => {
    updatePrrAttemptedCompensationRecords({
      variables: {
        recordsToUpdate: compensationRecords.map(({ compensationRecordId, caseId }) => ({ compensationRecordId, caseId }))
      }
    });
  };

  const handleCloseFailedRecordsWarning = () => {
    setFailedRecords([]);
    history.go(0);
  };

  return (
    <>
      <Modal
        isOpen={compensationRecords && compensationRecords.length > 0}
        onClose={onClose}
        title={<FormattedMessage id="prrRefund.prrAttemptedConfirmationTitle" />}
        subtitle={<FormattedMessage id="prrRefund.prrAttemptedConfirmationSubTitle" />}
        bottomRow={
          <ButtonWrapper>
            <ConfirmButton
              type="button"
              data-testid="prr-refund-attempted-compensate-tickets"
              data-cy="prr-refund-attempted-compensate-tickets"
              onClick={onContinue}
              loading={loading}
              disabled={loading}
            >
              <FormattedMessage id="prrRefund.compensateTickets" />
            </ConfirmButton>
            <ConfirmButton
              type="button"
              data-cy="prr-refund-attempted-update-starlight"
              data-testid="prr-refund-attempted-update-starlight"
              onClick={handleUpdateRecords}
              loading={loading}
              disabled={loading}
            >
              <FormattedMessage id="prrRefund.updateStarlight" />
            </ConfirmButton>
          </ButtonWrapper>
        }
      ></Modal>
      <Modal
        isOpen={failedRecords.length > 0}
        onClose={handleCloseFailedRecordsWarning}
        title={<FormattedMessage id="prrRefund.prrAttemptedFailedTitle" />}
        subtitle={
          <>
            {failedRecords?.[1]?.length > 0 ? (
              <FormattedMessage id="prrRefund.prrAttemptedUpdated" values={{ tickets: failedRecords[1].join(', ') }} />
            ) : null}
            {failedRecords?.[0]?.length > 0 ? (
              <FormattedMessage id="prrRefund.prrAttemptedFailedTickets" values={{ tickets: failedRecords[0].join(', ') }} />
            ) : null}
          </>
        }
      />
    </>
  );
};

PrrAttemptedConfirmation.propTypes = {
  compensationRecords: PropTypes.arrayOf(
    PropTypes.shape({ ticketNumber: PropTypes.string, compensationRecordId: PropTypes.string, caseId: PropTypes.string })
  ),
  onClose: PropTypes.func,
  onContinue: PropTypes.func,
  onOpenCompensationWarning: PropTypes.func
};

export default PrrAttemptedConfirmation;
